import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class StorageService {

  constructor() { }

  /**
   * Obtiene la referencia al almacenamiento (localStorage o sessionStorage).
   * @param storageType - Tipo de almacenamiento: 'local' para localStorage o 'session' para sessionStorage.
   * @returns Storage - La instancia del almacenamiento correspondiente.
   */
  private getStorage(storageType: 'local' | 'session'): Storage {
    return storageType === 'local' ? localStorage : sessionStorage;
  }

  /**
   * Método para almacenar información en el almacenamiento especificado.
   * @param key - Nombre clave bajo la cual se almacenará la información.
   * @param value - Información a almacenar.
   * @param storageType - Tipo de almacenamiento: 'local' o 'session'.
   * @returns void
   */
  set(key: string, value: any, storageType: 'local' | 'session' = 'local'): void {
    try {
      const storage = this.getStorage(storageType);
      const serializedValue = typeof value === 'string' ? value : JSON.stringify(value);
      storage.setItem(key, serializedValue);
    } catch (error) {
      console.error(`Error saving key "${key}" to ${storageType}Storage:`, error);
    }
  }

  /**
   * Método para obtener información desde el almacenamiento especificado.
   * @param key - Nombre clave de la información a obtener.
   * @param storageType - Tipo de almacenamiento: 'local' o 'session'.
   * @returns T | null - La información almacenada si existe, de lo contrario `null`.
   */
  get<T>(key: string, storageType: 'local' | 'session' = 'local'): T | string | null {
    const storage = this.getStorage(storageType);
    const value = storage.getItem(key);
    try {
      return value ? JSON.parse(value) : null; // Intenta parsear si es JSON
    } catch {
      return value; // Si no es JSON, devuelve el valor como string
    }
  }

  /**
   * Método para verificar si una clave existe en el almacenamiento especificado.
   * @param key - Nombre clave de la información.
   * @param storageType - Tipo de almacenamiento: 'local' o 'session'.
   * @returns boolean - `true` si la clave existe, de lo contrario `false`.
   */
  has(key: string, storageType: 'local' | 'session' = 'local'): boolean {
    const storage = this.getStorage(storageType);
    return storage.getItem(key) !== null;
  }

  /**
   * Método para eliminar un elemento específico del almacenamiento especificado.
   * @param key - Nombre clave de la información a eliminar.
   * @param storageType - Tipo de almacenamiento: 'local' o 'session'.
   * @returns void
   */
  delete(key: string, storageType: 'local' | 'session' = 'local'): void {
    const storage = this.getStorage(storageType);
    storage.removeItem(key);
  }

  /**
   * Método para limpiar todo el almacenamiento especificado.
   * @param storageType - Tipo de almacenamiento: 'local' o 'session'.
   * @returns void
   */
  clear(storageType: 'local' | 'session' = 'local'): void {
    const storage = this.getStorage(storageType);
    storage.clear();
  }
}
